import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { DriverViewModel, DriverNote } from "@app/shared/models/drivers/DriverViewModel.model";
import { ConfirmationService, MessageService } from "primeng/api";
import { authenticationService } from "@app/login/auth.service";
import { DriverService } from "@app/driver/shared/driver.service";
import { NewTripService } from "@app/trip-new/shared/trip.service";
import { StatesServices } from "@app/shared/services/states/states.service";
import { clientService } from "@app/client/shared/client.service";
import { ConfigService } from "@app/utilities/config/shared/config.service";

@Component({
  selector: "app-driver-notes",
  templateUrl: "./driver-notes.component.html",
  styleUrls: ["./driver-notes.component.css"],
})
export class DriverNotesComponent implements OnInit, OnChanges {
  @Input() driver: DriverViewModel;
  @Input() isEditMode: boolean;
  @Input() canEdit: boolean = false;

  @Output() hasChanges = new EventEmitter<boolean>();

  notes: DriverNote[] = [];
  filteredNotes: DriverNote[] = [];
  searchText: string = "";
  filterStatus: string = "";
  filterType: string = "";
  filterDateFrom: string = "";
  filterDateTo: string = "";
  showAddNoteModal: boolean = false;
  pageSize: number = 10;

  newNote: Partial<DriverNote> = {
    note: "",
    status: "Open",
    type: "",
    createdByUserName: "",
    dateCreatedUtc: new Date(),
  };

  noteTypes: string[] = [
    "General",
    "Driver Release",
    "Delays",
    "Breakdown & follow up",
    "Accident",
    "Incident",
  ];

  // Dropdown data
  tractors: any[] = [];
  trailers: any[] = [];
  clients: any[] = [];
  allClients: any[] = []; // All clients including bill-tos
  products: any[] = [];
  states: any[] = [];

  // Modal states
  showEmailModal: boolean = false;
  noteForEmail: DriverNote | null = null;

  // Email states
  emailingToSafety: boolean = false;
  emailedToSafety: boolean = false;
  emailToSafetyError: string = "";

  emailingFormToDriver: boolean = false;
  emailedFormToDriver: boolean = false;
  emailFormToDriverError: string = "";

  emailingToClient: boolean = false;
  emailedToClient: boolean = false;
  emailToClientError: string = "";

  emailingToDriver: boolean = false;
  emailedToDriver: boolean = false;
  emailToDriverError: string = "";

  // Email preview states
  showEmailPreview: boolean = false;
  currentEmailType: "toSafety" | "formToDriver" | "toClient" | "toDriver" | null = null;
  emailPreview: {
    to: string;
    ccEmails: string;
    subject: string;
    body: string;
  } = { to: "", ccEmails: "", subject: "", body: "" };
  emailAttachments: File[] = [];
  emailValidationErrors: { to: string; cc: string } = { to: "", cc: "" };

  private originalDriver: string;
  loading: boolean = false;
  driverNotesCcEmail: string = "omahadispatch@lgttransport.com"; // Default fallback

  constructor(
    private messageService: MessageService,
    private authService: authenticationService,
    private driverService: DriverService,
    private tripService: NewTripService,
    private statesService: StatesServices,
    private clientService: clientService,
    private configService: ConfigService
  ) {}

  ngOnInit() {
    this.originalDriver = JSON.stringify(this.driver);
    this.loadNotes();
    this.loadDropdownData();
    // Get US states from service
    this.states = this.statesService.getTwoLettersStates("US");
    // Load environment-specific CC email
    this.configService.getDriverNotesCcEmail().subscribe(
      (ccEmail: string) => {
        this.driverNotesCcEmail = ccEmail;
      },
      (error) => {
        console.error("Error loading CC email config:", error);
        // Keep default fallback value
      }
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["driver"] && !changes["driver"].firstChange) {
      this.originalDriver = JSON.stringify(this.driver);
      this.loadNotes();
      this.checkForChanges();
    }
  }

  loadNotes() {
    // For new drivers without an ID, use local notes array
    if (!this.driver.id) {
      if (this.driver.notes && Array.isArray(this.driver.notes)) {
        this.notes = [...this.driver.notes];
      } else {
        this.notes = [];
      }
      this.filterNotes();
      return;
    }

    // For existing drivers, fetch notes from API
    this.loading = true;
    this.driverService.getNotes(this.driver.id).subscribe(
      (notes) => {
        this.notes = notes || [];
        // Convert date strings to Date objects
        this.notes.forEach((note) => this.initNoteDateFields(note));
        this.driver.notes = [...this.notes];
        this.filterNotes();
        this.loading = false;
      },
      (error) => {
        console.error("Error loading notes:", error);
        this.notes = [];
        this.filterNotes();
        this.loading = false;
      }
    );
  }

  loadDropdownData() {
    this.tripService.getTractorsDropDown().subscribe((data: any) => {
      this.tractors = data || [];
    });
    this.tripService.getTrailersDropDown().subscribe((data: any) => {
      this.trailers = data || [];
    });
    this.tripService.getClientsDropDown().subscribe((data: any) => {
      this.allClients = data || [];
      // Filter to get only actual clients (those without clientId)
      this.clients = this.allClients.filter((c) => !c.clientId);
    });
    this.tripService.getProductsDropDown().subscribe((data: any) => {
      this.products = data || [];
    });
  }

  filterNotes() {
    this.filteredNotes = this.notes.filter((note) => {
      const searchLower = this.searchText?.toLowerCase() || "";
      const matchesSearch =
        !this.searchText ||
        (note.note || "").toLowerCase().includes(searchLower) ||
        (note.citationDescription || "").toLowerCase().includes(searchLower) ||
        (note.tractorNumber || "").toLowerCase().includes(searchLower) ||
        (note.trailerNumber || "").toLowerCase().includes(searchLower) ||
        (note.loadedEmpty || "").toLowerCase().includes(searchLower) ||
        (note.productName || "").toLowerCase().includes(searchLower) ||
        (note.accidentDate || "").toLowerCase().includes(searchLower) ||
        (note.accidentTime || "").toLowerCase().includes(searchLower) ||
        (note.accidentCity || "").toLowerCase().includes(searchLower) ||
        (note.accidentState || "").toLowerCase().includes(searchLower) ||
        (note.accidentLocation || "").toLowerCase().includes(searchLower) ||
        (note.weather || "").toLowerCase().includes(searchLower) ||
        (note.accidentDescription || "").toLowerCase().includes(searchLower) ||
        (note.damageDescription || "").toLowerCase().includes(searchLower) ||
        (note.assignedCustomer || "").toLowerCase().includes(searchLower) ||
        (note.photosToSafety || "").toLowerCase().includes(searchLower) ||
        (note.clientName || "").toLowerCase().includes(searchLower) ||
        (note.billToName || "").toLowerCase().includes(searchLower) ||
        (note.tripNumber || "").toLowerCase().includes(searchLower) ||
        (note.hosRemaining?.toString() || "").toLowerCase().includes(searchLower) ||
        (note.internalNotes || "").toLowerCase().includes(searchLower) ||
        (note.clientNotes || "").toLowerCase().includes(searchLower);
      const matchesStatus = !this.filterStatus || note.status === this.filterStatus;
      const matchesType = !this.filterType || note.type === this.filterType;

      // Date filter - compare local dates only (ignore time)
      let matchesDate = true;
      if (this.filterDateFrom || this.filterDateTo) {
        const noteDate = note.dateCreatedUtc ? new Date(note.dateCreatedUtc) : null;
        if (noteDate) {
          // Get local date string (YYYY-MM-DD) for comparison
          const noteDateStr = noteDate.toLocaleDateString("en-CA"); // Returns YYYY-MM-DD format
          if (this.filterDateFrom && noteDateStr < this.filterDateFrom) {
            matchesDate = false;
          }
          if (this.filterDateTo && noteDateStr > this.filterDateTo) {
            matchesDate = false;
          }
        } else {
          matchesDate = false;
        }
      }

      return matchesSearch && matchesStatus && matchesType && matchesDate;
    });

    // Sort: Notes with follow-up due today first, then by date created (newest first)
    this.filteredNotes.sort((a, b) => {
      const aFollowUpToday = this.isFollowUpDueToday(a);
      const bFollowUpToday = this.isFollowUpDueToday(b);

      // Follow-up due today notes come first
      if (aFollowUpToday && !bFollowUpToday) return -1;
      if (!aFollowUpToday && bFollowUpToday) return 1;

      // Then sort by date created (newest first)
      const aDate = a.dateCreatedUtc ? new Date(a.dateCreatedUtc).getTime() : 0;
      const bDate = b.dateCreatedUtc ? new Date(b.dateCreatedUtc).getTime() : 0;
      return bDate - aDate;
    });
  }

  openAddNoteModal() {
    this.newNote = {
      note: "",
      status: "Open",
      type: "",
      createdByUserName: this.authService.getUsername() ?? undefined,
      dateCreatedUtc: new Date(),
    };
    this.showAddNoteModal = true;
  }

  closeAddNoteModal() {
    this.showAddNoteModal = false;
  }

  isAccidentOrIncident(type?: string): boolean {
    return type === "Accident" || type === "Incident";
  }

  isDelayOrBreakdown(type?: string): boolean {
    return type === "Delays" || type === "Breakdown & follow up";
  }

  isBreakdown(type?: string): boolean {
    return type === "Breakdown & follow up";
  }

  isDriverRelease(type?: string): boolean {
    return type === "Driver Release";
  }

  isFollowUpDueToday(note: DriverNote): boolean {
    if (!note.followUpDate) return false;
    const today = new Date();
    const followUp = new Date(note.followUpDate);
    return (
      today.getFullYear() === followUp.getFullYear() &&
      today.getMonth() === followUp.getMonth() &&
      today.getDate() === followUp.getDate()
    );
  }

  onNoteSaved(note: DriverNote) {
    // Save via API or add to local notes array
    const noteToSave: DriverNote = {
      ...note,
      driverId: this.driver.id?.toString(),
    };

    if (this.driver.id) {
      this.driverService.saveNote(noteToSave).subscribe(
        (savedNote: any) => {
          this.initNoteDateFields(savedNote);
          // Merge: prefer server response but fall back to client-resolved display names
          // when the API doesn't eagerly load navigation properties (e.g. productName,
          // clientName, billToName, tractorNumber, trailerNumber).
          const mergedNote: DriverNote = {
            ...savedNote,
            productName: savedNote.productName || noteToSave.productName,
            clientName: savedNote.clientName || noteToSave.clientName,
            billToName: savedNote.billToName || noteToSave.billToName,
            tractorNumber: savedNote.tractorNumber || noteToSave.tractorNumber,
            trailerNumber: savedNote.trailerNumber || noteToSave.trailerNumber,
          };
          if (noteToSave.id) {
            const index = this.notes.findIndex((n) => n.id === noteToSave.id);
            if (index > -1) {
              this.notes = [
                ...this.notes.slice(0, index),
                mergedNote,
                ...this.notes.slice(index + 1),
              ];
            }
          } else {
            this.notes = [mergedNote, ...this.notes];
          }
          this.driver.notes = [...this.notes];
          this.filterNotes();
          this.closeAddNoteModal();

          // Show email modal for accident/incident or delay/breakdown (only for new notes)
          if (
            !noteToSave.id &&
            (this.isAccidentOrIncident(savedNote.type) || this.isDelayOrBreakdown(savedNote.type))
          ) {
            this.noteForEmail = savedNote;
            this.showEmailModal = true;
          }
        },
        (error) => {
          console.error("Error saving note:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: noteToSave.id
              ? "Failed to update note. Please try again."
              : "Failed to save note. Please try again.",
          });
        }
      );
    } else {
      this.notes.unshift(noteToSave);
      this.saveNotes();
      this.filterNotes();
      this.closeAddNoteModal();

      if (this.isAccidentOrIncident(noteToSave.type) || this.isDelayOrBreakdown(noteToSave.type)) {
        this.noteForEmail = noteToSave;
        this.showEmailModal = true;
      }
    }
  }

  editNote(note: any) {
    // Open the add note modal with the existing note data
    this.newNote = { ...note };
    // Ensure dates are properly initialized
    this.initNoteDateFields(this.newNote);
    this.showAddNoteModal = true;
  }

  confirmDelete(note: any) {
    if (!confirm("Are you sure you want to delete this note?")) return;
    this.deleteNote(note);
  }

  deleteNote(note: any) {
    // Only delete via API if driver exists and note has an ID
    if (this.driver.id && note.id) {
      this.driverService.deleteNote(note.id).subscribe(
        () => {
          const index = this.notes.indexOf(note);
          if (index > -1) {
            this.notes.splice(index, 1);
            this.driver.notes = [...this.notes];
            this.filterNotes();
          }
          this.messageService.add({
            severity: "success",
            summary: "Note Deleted",
            detail: "Note has been deleted successfully.",
          });
        },
        (error) => {
          console.error("Error deleting note:", error);
          const errorMessage = error.error?.error || "Failed to delete note. Please try again.";
          this.messageService.add({
            severity: "error",
            summary: "Cannot Delete Note",
            detail: errorMessage,
          });
        }
      );
    } else {
      // For new drivers, just remove from local array
      const index = this.notes.indexOf(note);
      if (index > -1) {
        this.notes.splice(index, 1);
        this.saveNotes();
        this.filterNotes();
      }
    }
  }

  onNoteChange(note: any) {
    if (note.isEditing) {
      this.saveNotes();
    }
  }

  saveNotes() {
    this.driver.notes = [...this.notes];
    this.onFieldChange();
  }

  onFieldChange() {
    this.checkForChanges();
  }

  private checkForChanges() {
    const currentDriver = JSON.stringify(this.driver);
    const changed = currentDriver !== this.originalDriver;
    this.hasChanges.emit(changed);
  }

  closeEmailModal() {
    this.showEmailModal = false;
    this.noteForEmail = null;
    // Reset email states
    this.emailingToSafety = false;
    this.emailedToSafety = false;
    this.emailToSafetyError = "";
    this.emailingFormToDriver = false;
    this.emailedFormToDriver = false;
    this.emailFormToDriverError = "";
    this.emailingToClient = false;
    this.emailedToClient = false;
    this.emailToClientError = "";
    this.emailingToDriver = false;
    this.emailedToDriver = false;
    this.emailToDriverError = "";
  }

  emailToSafety() {
    const note = this.noteForEmail || (this.newNote as DriverNote);
    if (!note || !note.id) return;

    const driverName = `${this.driver.name} ${this.driver.lastName}`;
    const noteDetails = this.buildAccidentNoteDetails(note);

    this.emailPreview = {
      to: "safety@lgttransport.com",
      ccEmails: this.driverNotesCcEmail,
      subject: `${note.type} Report - Driver ${driverName}`,
      body: `Driver: ${driverName}\n\n${noteDetails}`,
    };
    this.currentEmailType = "toSafety";
    this.showEmailPreview = true;
  }

  sendEmailToSafety() {
    const note = this.noteForEmail || (this.newNote as DriverNote);
    if (!note || !note.id) return;

    this.emailingToSafety = true;
    this.emailToSafetyError = "";

    this.driverService
      .sendCustomEmail(
        note.id,
        this.emailPreview.to,
        this.emailPreview.subject,
        this.emailPreview.body,
        "toSafety",
        this.emailPreview.ccEmails,
        this.emailAttachments
      )
      .subscribe(
        (response: any) => {
          this.emailingToSafety = false;
          this.emailedToSafety = true;
          this.showEmailPreview = false;
          this.emailAttachments = [];

          // Update the note with the returned data
          if (response.note) {
            this.updateNoteInList(response.note);
          }

          this.messageService.add({
            severity: "success",
            summary: "Email Sent",
            detail: `Note has been emailed to ${this.emailPreview.to}`,
          });
        },
        (error) => {
          this.emailingToSafety = false;
          this.emailToSafetyError = error.error?.error || "Failed to send email. Please try again.";
        }
      );
  }

  emailFormToDriver() {
    const note = this.noteForEmail || (this.newNote as DriverNote);
    if (!note || !note.id) return;

    const driverName = `${this.driver.name} ${this.driver.lastName}`;
    const formLink =
      "https://docs.google.com/forms/d/e/1FAIpQLSecnRaMbaTlvEyjgeHzM2Qo4tDFfFAjNisgxSDpl8K4SfPPsw/viewform";

    this.emailPreview = {
      to: this.driver.email || "",
      ccEmails: this.driverNotesCcEmail,
      subject: `${note.type} Form Required - ${driverName}`,
      body: `${driverName},\n\nPlease complete the following ${note.type?.toLowerCase()} form:\n\n${formLink}\n\nThank you.`,
    };
    this.currentEmailType = "formToDriver";
    this.showEmailPreview = true;
  }

  sendEmailFormToDriver() {
    const note = this.noteForEmail || (this.newNote as DriverNote);
    if (!note || !note.id) return;

    this.emailingFormToDriver = true;
    this.emailFormToDriverError = "";

    this.driverService
      .sendCustomEmail(
        note.id,
        this.emailPreview.to,
        this.emailPreview.subject,
        this.emailPreview.body,
        "formToDriver",
        this.emailPreview.ccEmails,
        this.emailAttachments
      )
      .subscribe(
        (response: any) => {
          this.emailingFormToDriver = false;
          this.emailedFormToDriver = true;
          this.showEmailPreview = false;
          this.emailAttachments = [];

          // Update the note with the returned data
          if (response.note) {
            this.updateNoteInList(response.note);
          }

          this.messageService.add({
            severity: "success",
            summary: "Form Sent",
            detail: `Accident form has been emailed to ${this.emailPreview.to}`,
          });
        },
        (error) => {
          this.emailingFormToDriver = false;
          this.emailFormToDriverError =
            error.error?.error || "Failed to send form. Please try again.";
        }
      );
  }

  emailToClient() {
    const note = this.noteForEmail || (this.newNote as DriverNote);
    if (!note || !note.id) return;

    // Fetch the bill-to email from the server
    if (note.billToId) {
      this.clientService.getClientInfo(note.billToId.toString()).subscribe(
        (billTo: any) => {
          const clientEmail = billTo?.email || billTo?.invoiceEmail || "";
          this.openEmailToClientPreview(note, clientEmail);
        },
        (error) => {
          console.error("Error fetching bill-to info:", error);
          // Fall back to opening with empty email
          this.openEmailToClientPreview(note, "");
        }
      );
    } else {
      this.openEmailToClientPreview(note, "");
    }
  }

  private openEmailToClientPreview(note: DriverNote, clientEmail: string) {
    const driverName = `${this.driver.name} ${this.driver.lastName}`;
    const delayDetails = this.buildDelayNoteDetails(note);

    // Format the note type for the message (e.g., "Delays" -> "delay", "Breakdown & follow up" -> "breakdown")
    const noteTypeForMessage = note.type?.toLowerCase().includes("delay") ? "delay" : "breakdown";

    this.emailPreview = {
      to: clientEmail,
      ccEmails: this.driverNotesCcEmail,
      subject: `${note.type} Notification - Trip ${note.tripNumber || "N/A"}`,
      body: `We wanted to inform you of a ${noteTypeForMessage}:\n\nDriver: ${driverName}\nTrip: ${
        note.tripNumber || "N/A"
      }\n\n${delayDetails}\n\nThank you for your understanding.`,
    };
    this.currentEmailType = "toClient";
    this.showEmailPreview = true;
  }

  sendEmailToClient() {
    const note = this.noteForEmail || (this.newNote as DriverNote);
    if (!note || !note.id) return;

    this.emailingToClient = true;
    this.emailToClientError = "";

    this.driverService
      .sendCustomEmail(
        note.id,
        this.emailPreview.to,
        this.emailPreview.subject,
        this.emailPreview.body,
        "toClient",
        this.emailPreview.ccEmails,
        this.emailAttachments
      )
      .subscribe(
        (response: any) => {
          this.emailingToClient = false;
          this.emailedToClient = true;
          this.showEmailPreview = false;
          this.emailAttachments = [];

          // Update the note with the returned data
          if (response.note) {
            this.updateNoteInList(response.note);
          }

          this.messageService.add({
            severity: "success",
            summary: "Email Sent",
            detail: `Note has been emailed to ${this.emailPreview.to}`,
          });
        },
        (error) => {
          this.emailingToClient = false;
          this.emailToClientError = error.error?.error || "Failed to send email. Please try again.";
        }
      );
  }

  emailToDriver() {
    const note = this.noteForEmail || (this.newNote as DriverNote);
    if (!note || !note.id) return;

    const driverName = `${this.driver.name} ${this.driver.lastName}`;
    const delayDetails = this.buildDelayNoteDetails(note);

    this.emailPreview = {
      to: this.driver.email || "",
      ccEmails: this.driverNotesCcEmail,
      subject: `${note.type} Notification - Trip ${note.tripNumber || "N/A"}`,
      body: `${driverName},\n\nThis is a notification regarding your current trip:\n\nTrip Number: ${
        note.tripNumber || "N/A"
      }\n\n${delayDetails}\n\nPlease contact dispatch if you have any questions.\n\nThank you.`,
    };
    this.currentEmailType = "toDriver";
    this.showEmailPreview = true;
  }

  sendEmailToDriver() {
    const note = this.noteForEmail || (this.newNote as DriverNote);
    if (!note || !note.id) return;

    this.emailingToDriver = true;
    this.emailToDriverError = "";

    this.driverService
      .sendCustomEmail(
        note.id,
        this.emailPreview.to,
        this.emailPreview.subject,
        this.emailPreview.body,
        "toDriver",
        this.emailPreview.ccEmails,
        this.emailAttachments
      )
      .subscribe(
        (response: any) => {
          this.emailingToDriver = false;
          this.emailedToDriver = true;
          this.showEmailPreview = false;
          this.emailAttachments = [];

          // Update the note with the returned data
          if (response.note) {
            this.updateNoteInList(response.note);
          }

          this.messageService.add({
            severity: "success",
            summary: "Email Sent",
            detail: `Note has been emailed to ${this.emailPreview.to}`,
          });
        },
        (error) => {
          this.emailingToDriver = false;
          this.emailToDriverError = error.error?.error || "Failed to send email. Please try again.";
        }
      );
  }

  buildAccidentNoteDetails(note: DriverNote): string {
    let details = [];
    if (note.tractorNumber) details.push(`Tractor: ${note.tractorNumber.toUpperCase()}`);
    if (note.trailerNumber) details.push(`Trailer: ${note.trailerNumber.toUpperCase()}`);
    if (note.loadedEmpty) details.push(`Loaded/Empty: ${note.loadedEmpty.toUpperCase()}`);
    if (note.productName) details.push(`Product: ${note.productName.toUpperCase()}`);
    if (note.accidentDate) details.push(`Date: ${note.accidentDate}`);
    if (note.accidentTime) details.push(`Time: ${note.accidentTime}`);
    if (note.accidentCity || note.accidentState)
      details.push(
        `Location: ${(note.accidentCity || "").toUpperCase()}, ${(
          note.accidentState || ""
        ).toUpperCase()}`
      );
    if (note.weather) details.push(`Weather: ${note.weather.toUpperCase()}`);
    if (note.accidentLocation)
      details.push(`Accident Location: ${note.accidentLocation.toUpperCase()}`);
    if (note.accidentDescription)
      details.push(`Description: ${note.accidentDescription.toUpperCase()}`);
    if (note.damageDescription) details.push(`Damage: ${note.damageDescription.toUpperCase()}`);
    if (note.anyInjuries !== undefined)
      details.push(`Injuries/Tow-aways/Fatalities: ${note.anyInjuries ? "YES" : "NO"}`);
    if (note.anotherVehicleInvolved !== undefined)
      details.push(`Another Vehicle: ${note.anotherVehicleInvolved ? "YES" : "NO"}`);
    if (note.driverCitation !== undefined)
      details.push(`Citation: ${note.driverCitation ? "YES" : "NO"}`);
    if (note.citationDescription)
      details.push(`Citation Details: ${note.citationDescription.toUpperCase()}`);
    if (note.assignedCustomer)
      details.push(`Assigned Customer: ${note.assignedCustomer.toUpperCase()}`);
    if (note.photosToSafety) details.push(`Photos to Safety: ${note.photosToSafety.toUpperCase()}`);
    if (note.note) details.push(`Notes: ${note.note}`);
    return details.join("\n");
  }

  buildDelayNoteDetails(note: DriverNote): string {
    let details = [];
    if (note.tripNumber) details.push(`Trip Number: ${note.tripNumber}`);
    if (note.customerReferenceNumber)
      details.push(`Customer Reference: ${note.customerReferenceNumber}`);
    if (note.eta) details.push(`ETA: ${note.eta}`);
    if (note.truckTrailerIssue) details.push(`Issue Type: ${note.truckTrailerIssue}`);
    if (note.loadedEmpty) details.push(`Loaded/Empty: ${note.loadedEmpty.toUpperCase()}`);
    if (note.productName) details.push(`Product: ${note.productName.toUpperCase()}`);
    if (note.hosRemaining !== undefined) details.push(`HOS Remaining: ${note.hosRemaining} hours`);
    if (note.clientNotes) details.push(`Client Notes: ${note.clientNotes.toUpperCase()}`);
    return details.join("\n");
  }

  cancelEmailPreview() {
    this.showEmailPreview = false;
    this.currentEmailType = null;
    this.emailValidationErrors = { to: "", cc: "" };
    this.emailAttachments = [];
  }

  // File attachment methods
  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files[i];
        if (file.type.startsWith("image/")) {
          this.emailAttachments.push(file);
        }
      }
    }
    // Reset the input so the same file can be selected again
    input.value = "";
  }

  removeAttachment(index: number) {
    this.emailAttachments.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  clearEmailValidationError(field: "to" | "cc") {
    this.emailValidationErrors[field] = "";
  }

  isValidEmail(email: string): boolean {
    if (!email || !email.trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  validateEmailFields(): boolean {
    this.emailValidationErrors = { to: "", cc: "" };
    let isValid = true;

    // Validate 'to' email
    if (!this.emailPreview.to || !this.emailPreview.to.trim()) {
      this.emailValidationErrors.to = "Recipient email is required.";
      isValid = false;
    } else if (!this.isValidEmail(this.emailPreview.to)) {
      this.emailValidationErrors.to = "Please enter a valid email address.";
      isValid = false;
    }

    // Validate CC emails (optional, but if provided must be valid)
    if (this.emailPreview.ccEmails && this.emailPreview.ccEmails.trim()) {
      const ccEmails = this.emailPreview.ccEmails
        .split(/[;,]/)
        .map((e) => e.trim())
        .filter((e) => e);
      for (const cc of ccEmails) {
        if (!this.isValidEmail(cc)) {
          this.emailValidationErrors.cc = `Invalid email: ${cc}`;
          isValid = false;
          break;
        }
      }
    }

    return isValid;
  }

  confirmSendEmail() {
    if (!this.validateEmailFields()) {
      return;
    }

    switch (this.currentEmailType) {
      case "toSafety":
        this.sendEmailToSafety();
        break;
      case "formToDriver":
        this.sendEmailFormToDriver();
        break;
      case "toClient":
        this.sendEmailToClient();
        break;
      case "toDriver":
        this.sendEmailToDriver();
        break;
    }
  }

  updateNoteInList(updatedNote: DriverNote) {
    // Update the note in the notes array
    const index = this.notes.findIndex((n) => n.id === updatedNote.id);
    if (index > -1) {
      // Preserve display values and merge with updated data
      this.notes[index] = { ...this.notes[index], ...updatedNote };
      this.initNoteDateFields(this.notes[index]);
      this.driver.notes = [...this.notes];
      this.filterNotes();
    }
    // Also update noteForEmail if it matches
    if (this.noteForEmail && this.noteForEmail.id === updatedNote.id) {
      this.noteForEmail = { ...this.noteForEmail, ...updatedNote };
      this.initNoteDateFields(this.noteForEmail);
    }
    // Update newNote if editing the same note
    if (this.newNote.id === updatedNote.id) {
      this.newNote = { ...this.newNote, ...updatedNote };
      this.initNoteDateFields(this.newNote);
    }
  }

  // Date field initialization
  initNoteDateFields(note: Partial<DriverNote>) {
    note.followUpDate = this.convertToDate(note.followUpDate);
  }

  convertToDate(date: any): Date | undefined {
    return date ? new Date(date) : undefined;
  }
}
